import { useState, useCallback, useEffect } from 'react';
import { Card, CardHeader, Button, Badge, Input, Pagination, LoadingSkeleton, Tabs } from '@nexora/design-system';
import {
  useChartOfAccounts,
  useCreateAccount,
  useUpdateAccount,
  useDeleteAccount,
  useJournalEntries,
  useCreateEntry,
  useAccountingCtx,
  type Account,
  type AccountInput,
  type JournalEntry,
  type JournalEntryInput,
  ACCOUNT_TYPE_LABEL,
  accountTypeBadgeVariant,
} from './useAccounting';
import { TrialBalanceTab, IncomeStatementTab } from './FinancialStatementsPage';
import { AccountFormModal } from './AccountFormModal';
import { EntryFormModal } from './EntryFormModal';
import { formatCurrency, formatDate } from '../../shared/utils/format';

/* ── Types ── */

type AccountingTab = 'chart' | 'journal' | 'trial-balance' | 'income-statement';

const ACCOUNTING_TABS = [
  { key: 'chart' as AccountingTab, label: 'Plano de Contas' },
  { key: 'journal' as AccountingTab, label: 'Lançamentos' },
  { key: 'trial-balance' as AccountingTab, label: 'Balancete' },
  { key: 'income-statement' as AccountingTab, label: 'DRE' },
];

/* ── Toast helper ── */

interface ToastState {
  message: string;
  variant: 'success' | 'error';
}

/* ── Account TreeNode ── */

function AccountTreeNode({
  account,
  depth,
  onEdit,
  onDelete,
}: {
  account: Account;
  depth: number;
  onEdit: (acc: Account) => void;
  onDelete: (id: string) => void;
}): JSX.Element {
  const [expanded, setExpanded] = useState(depth < 1);
  const hasChildren = account.children && account.children.length > 0;

  return (
    <>
      <tr
        className="chart-row"
        style={{ cursor: hasChildren ? 'pointer' : 'default' }}
        onClick={() => { if (hasChildren) setExpanded(!expanded); }}
      >
        <td
          style={{
            padding: '0.625rem 1rem',
            borderBottom: '1px solid #eceef0',
            paddingLeft: `${1 + depth * 1.5}rem`,
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
            {hasChildren && (
              <span style={{ fontSize: '0.625rem', color: '#74777f', width: '12px', display: 'inline-block' }}>
                {expanded ? '▼' : '▶'}
              </span>
            )}
            <span style={{ fontWeight: depth === 0 ? 600 : 400 }}>{account.code}</span>
          </span>
        </td>
        <td style={{ padding: '0.625rem 1rem', borderBottom: '1px solid #eceef0' }}>
          {account.name}
        </td>
        <td style={{ padding: '0.625rem 1rem', borderBottom: '1px solid #eceef0' }}>
          <Badge variant={accountTypeBadgeVariant(account.type)} size="sm">
            {ACCOUNT_TYPE_LABEL[account.type]}
          </Badge>
        </td>
        <td style={{ padding: '0.625rem 1rem', borderBottom: '1px solid #eceef0', textAlign: 'right', fontWeight: 600 }}>
          {formatCurrency(account.balance)}
        </td>
        <td style={{ padding: '0.625rem 1rem', borderBottom: '1px solid #eceef0', whiteSpace: 'nowrap' }}>
          <Button variant="ghost" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); onEdit(account); }}>
            Editar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onDelete(account.id); }}
            style={{ color: '#ba1a1a' }}
          >
            Excluir
          </Button>
        </td>
      </tr>
      {expanded && hasChildren &&
        account.children!.map((child) => (
          <AccountTreeNode key={child.id} account={child} depth={depth + 1} onEdit={onEdit} onDelete={onDelete} />
        ))}
    </>
  );
}

/* ── Chart of Accounts Tab ── */

function ChartTab({
  showToast,
}: {
  showToast: (message: string, variant: 'success' | 'error') => void;
}): JSX.Element {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | undefined>(undefined);

  const ctx = useAccountingCtx();
  const { data: accounts, isLoading, error } = useChartOfAccounts(ctx);
  const createMutation = useCreateAccount(ctx);
  const updateMutation = useUpdateAccount();
  const deleteMutation = useDeleteAccount(ctx);

  const handleCreate = useCallback(
    async (input: AccountInput) => {
      try {
        await createMutation.mutateAsync(input);
        showToast('Conta criada com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao criar conta.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [createMutation, showToast],
  );

  const handleUpdate = useCallback(
    async (input: AccountInput) => {
      if (!editingAccount) return;
      try {
        await updateMutation.mutateAsync({ id: editingAccount.id, ...input });
        showToast('Conta atualizada com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao atualizar conta.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [editingAccount, updateMutation, showToast],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (!window.confirm('Tem certeza que deseja excluir esta conta?')) return;
      try {
        await deleteMutation.mutateAsync(id);
        showToast('Conta excluida com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao excluir conta.';
        showToast(msg, 'error');
      }
    },
    [deleteMutation, showToast],
  );

  const handleEdit = useCallback((acc: Account) => {
    setEditingAccount(acc);
    setModalOpen(true);
  }, []);

  const handleNew = useCallback(() => {
    setEditingAccount(undefined);
    setModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setEditingAccount(undefined);
  }, []);

  const safeAccounts = accounts ?? [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#191c1e' }}>
          Plano de Contas
          <span className="font-body-md text-body-md text-on-surface-variant font-normal ml-sm">
            ({safeAccounts.length} contas)
          </span>
        </h2>
        <Button variant="primary" onClick={handleNew}>+ Nova Conta</Button>
      </div>

      <Card variant="outlined" padding="0">
        <CardHeader className="px-md pt-md">
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Plano de contas</h3>
        </CardHeader>
        {isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: '#43474e' }}>
            <LoadingSkeleton />
            <span style={{ fontSize: '0.875rem' }}>Carregando plano de contas...</span>
          </div>
        )}

        {error && !isLoading && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#ba1a1a' }}>
            <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar plano de contas</p>
            <p style={{ fontSize: '0.875rem', margin: 0, color: '#74777f' }}>
              {error instanceof Error ? error.message : 'Erro desconhecido'}
            </p>
            <Button variant="outline" size="sm" onClick={() => window.location.reload()} style={{ marginTop: '1rem' }}>
              Tentar novamente
            </Button>
          </div>
        )}

        {!isLoading && !error && safeAccounts.length === 0 && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#74777f' }}>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem' }}>Nenhuma conta cadastrada.</p>
            <Button variant="outline" size="sm" onClick={handleNew}>Criar primeira conta</Button>
          </div>
        )}

        {!isLoading && !error && safeAccounts.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  {['Código', 'Nome', 'Tipo', 'Saldo', 'Ações'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: h === 'Saldo' ? 'right' : 'left',
                        padding: '0.75rem 1rem',
                        background: '#f2f4f6',
                        color: '#43474e',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2px solid #e0e3e5',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {safeAccounts.map((acc) => (
                  <AccountTreeNode key={acc.id} account={acc} depth={0} onEdit={handleEdit} onDelete={handleDelete} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <AccountFormModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        account={editingAccount}
        accounts={safeAccounts}
        onSubmit={editingAccount ? handleUpdate : handleCreate}
      />
    </div>
  );
}

/* ── Journal Entry Row (expandable) ── */

function JournalEntryRow({ entry }: { entry: JournalEntry }): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  const totalDebit = entry.lines.reduce((s, l) => s + l.debit, 0);
  const totalCredit = entry.lines.reduce((s, l) => s + l.credit, 0);

  return (
    <>
      <tr className="journal-row" style={{ cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
        <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', whiteSpace: 'nowrap' }}>
          {formatDate(entry.date)}
        </td>
        <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
          {entry.description}
        </td>
        <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', textAlign: 'right', fontWeight: 600 }}>
          {formatCurrency(totalDebit)}
        </td>
        <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', textAlign: 'right', fontWeight: 600 }}>
          {formatCurrency(totalCredit)}
        </td>
        <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', textAlign: 'center' }}>
          <span style={{ fontSize: '0.75rem', color: '#74777f' }}>{expanded ? '▲' : '▼'}</span>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={5} style={{ padding: '0 1rem 0.75rem', background: '#f2f4f6' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#43474e', fontWeight: 600, borderBottom: '1px solid #e0e3e5' }}>Conta</th>
                  <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: '#43474e', fontWeight: 600, borderBottom: '1px solid #e0e3e5' }}>Débito</th>
                  <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: '#43474e', fontWeight: 600, borderBottom: '1px solid #e0e3e5' }}>Crédito</th>
                </tr>
              </thead>
              <tbody>
                {entry.lines.map((line) => (
                  <tr key={line.id}>
                    <td style={{ padding: '0.375rem 0.75rem', borderBottom: '1px solid #f2f4f6' }}>
                      <span style={{ fontWeight: 500 }}>{line.accountCode}</span> - {line.accountName}
                    </td>
                    <td style={{ padding: '0.375rem 0.75rem', borderBottom: '1px solid #f2f4f6', textAlign: 'right', color: line.debit > 0 ? '#2e7d32' : undefined }}>
                      {line.debit > 0 ? formatCurrency(line.debit) : '-'}
                    </td>
                    <td style={{ padding: '0.375rem 0.75rem', borderBottom: '1px solid #f2f4f6', textAlign: 'right', color: line.credit > 0 ? '#002045' : undefined }}>
                      {line.credit > 0 ? formatCurrency(line.credit) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

/* ── Journal Tab ── */

function JournalTab({
  showToast,
}: {
  showToast: (message: string, variant: 'success' | 'error') => void;
}): JSX.Element {
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const ctx = useAccountingCtx();
  const { data, isLoading, error } = useJournalEntries(ctx, {
    page,
    limit: 20,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  });
  const { data: accounts } = useChartOfAccounts(ctx);
  const createMutation = useCreateEntry(ctx);

  const handleCreate = useCallback(
    async (input: JournalEntryInput) => {
      try {
        await createMutation.mutateAsync(input);
        showToast('Lançamento criado com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao criar lancamento.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [createMutation, showToast],
  );

  const handleFilter = useCallback(() => {
    setPage(1);
  }, []);

  const entries = data?.data ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#191c1e' }}>
          Lançamentos
          {data && (
            <span className="font-body-md text-body-md text-on-surface-variant font-normal ml-sm">
              ({data.total} lancamentos)
            </span>
          )}
        </h2>
        <Button variant="primary" onClick={() => setModalOpen(true)}>+ Novo Lançamento</Button>
      </div>

      <Card padding="1rem" variant="outlined" className="filter-card">
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '0 0 180px' }}>
            <Input label="Data inicio" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div style={{ flex: '0 0 180px' }}>
            <Input label="Data fim" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <Button variant="secondary" size="sm" onClick={handleFilter}>Filtrar</Button>
          {(startDate || endDate) && (
            <Button variant="ghost" size="sm" onClick={() => { setStartDate(''); setEndDate(''); setPage(1); }}>Limpar</Button>
          )}
        </div>
      </Card>

      <Card variant="outlined" padding="0">
        <CardHeader
          className="px-md pt-md"
          action={
            <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
              {data?.total ?? 0} {(data?.total ?? 0) === 1 ? 'lançamento' : 'lançamentos'}
            </span>
          }
        >
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Lançamentos</h3>
        </CardHeader>
        {isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: '#43474e' }}>
            <LoadingSkeleton />
            <span style={{ fontSize: '0.875rem' }}>Carregando lancamentos...</span>
          </div>
        )}

        {error && !isLoading && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#ba1a1a' }}>
            <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar lancamentos</p>
            <p style={{ fontSize: '0.875rem', margin: 0, color: '#74777f' }}>
              {error instanceof Error ? error.message : 'Erro desconhecido'}
            </p>
            <Button variant="outline" size="sm" onClick={() => window.location.reload()} style={{ marginTop: '1rem' }}>
              Tentar novamente
            </Button>
          </div>
        )}

        {!isLoading && !error && entries.length === 0 && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#74777f' }}>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem' }}>Nenhum lancamento encontrado.</p>
            <Button variant="outline" size="sm" onClick={() => setModalOpen(true)}>Criar primeiro lancamento</Button>
          </div>
        )}

        {!isLoading && !error && entries.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  {['Data', 'Descrição', 'Total Débito', 'Total Crédito', ''].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: ['Total Débito', 'Total Crédito'].includes(h) ? 'right' : 'left',
                        padding: '0.75rem 1rem',
                        background: '#f2f4f6',
                        color: '#43474e',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2px solid #e0e3e5',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <JournalEntryRow key={entry.id} entry={entry} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && !error && totalPages > 1 && (
          <div style={{ padding: '1rem', borderTop: '1px solid #eceef0' }}>
            <Pagination page={page} totalPages={totalPages} onChange={(p) => setPage(p)} />
          </div>
        )}
      </Card>

      <EntryFormModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        accounts={accounts ?? []}
        onSubmit={handleCreate}
      />
    </div>
  );
}

/* ── DRE (Income Statement) Tab ── */

export function AccountingPage(): JSX.Element {
  const [activeTab, setActiveTab] = useState<AccountingTab>('chart');
  const [toast, setToast] = useState<ToastState | null>(null);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  return (
    <div className="module-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Contabilidade</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Plano de contas, lançamentos e balanços.</p>
        </div>
      </div>

      <Tabs
        tabs={ACCOUNTING_TABS}
        value={activeTab}
        onChange={(key) => setActiveTab(key as AccountingTab)}
        variant="underline"
        aria-label="Abas de contabilidade"
      />

      <div className="module-content">
        {activeTab === 'chart' && <ChartTab showToast={showToast} />}
        {activeTab === 'journal' && <JournalTab showToast={showToast} />}
        {activeTab === 'trial-balance' && <TrialBalanceTab />}
        {activeTab === 'income-statement' && <IncomeStatementTab />}
      </div>

      {toast && (
        <div
          role="alert"
          className="toast-notification"
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            right: '1.5rem',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            background: toast.variant === 'success' ? '#d3f5d6' : '#ffdad6',
            border: `1px solid ${toast.variant === 'success' ? '#b6ebbb' : '#ffb4ab'}`,
            color: toast.variant === 'success' ? '#2e7d32' : '#ba1a1a',
            fontSize: '0.875rem',
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 10000,
            maxWidth: '400px',
          }}
        >
          {toast.message}
          <button
            onClick={() => setToast(null)}
            aria-label="Fechar"
            style={{
              marginLeft: '0.75rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'inherit',
              fontSize: '1rem',
              padding: '0',
              lineHeight: 1,
            }}
          >
            &times;
          </button>
        </div>
      )}

      <style>{`
        .module-page { padding: 1.5rem; }
        .module-content { margin-top: 1.5rem; }
        .chart-row:hover td { background: #eceef0; }
        .journal-row:hover td { background: #eceef0; }
        .statement-row:hover td { background: #eceef0; }
        .filter-card { margin-bottom: 1rem; }
        @keyframes toast-slide-in {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}